import { useEffect, useMemo } from 'react';
import {
  AgentConfig,
  AgentFeatureFlags,
  AgentPermissions,
  AgentReferenceData,
} from '../Pages/AgentHelpers';
import { AssistantFrameApi } from '../Services/AgentApiProviderTypes';
import AgentFramesPicker from './AgentFramesPicker';
import {
  CustomInput,
  CustomNumberInput,
  CustomSelect,
  CustomSlider,
  CustomToggle,
} from './CustomFormElements';
import { FetchItemsParams, FetchItemsResult } from './ItemsPickerModalTypes';

// ── Props ───────────────────────────────────────────────────────

interface AgentSettingsProps {
  config: AgentConfig;
  data: AgentReferenceData;
  permissions: AgentPermissions;
  featureFlags: AgentFeatureFlags;
  onChange: <K extends keyof AgentConfig>(key: K, value: AgentConfig[K]) => void;
  fetchFrames: (params: FetchItemsParams) => Promise<FetchItemsResult<AssistantFrameApi>>;
}

// ── Component ───────────────────────────────────────────────────

function AgentSettings({
  config,
  data,
  permissions,
  featureFlags,
  onChange,
  fetchFrames,
}: AgentSettingsProps) {
  const {
    isDisabled,
    isOrganizationsPermission,
    isDocumentPermission,
    disableCollections,
    collectionTextWarning,
  } = permissions;

  const {
    isDocumentFlag,
    isProductFlag,
    isInformationPackageFlag,
    isNotificationFlag,
    isConnectionFlag,
  } = featureFlags;

  const { options } = data;

  const selectedModel = useMemo(
    () => options?.models.find((m) => m.value === config.modelName) ?? null,
    [options, config.modelName],
  );

  const availableReasoningOptions = useMemo(
    () => selectedModel?.reasoning_options ?? [],
    [selectedModel],
  );
  const showTemperature = selectedModel?.parametrization.includes('temperature') ?? false;

  const availableSkills = useMemo(() => {
    if (!options) return [];
    return options.skills.filter((skill) => {
      if (!skill.built_in) return true;
      if (!selectedModel || !skill.required_feature) return false;
      return selectedModel.features.includes(skill.required_feature);
    });
  }, [options, selectedModel]);

  const onSelect =
    <K extends keyof AgentConfig>(key: K) =>
    (val: string | string[]) =>
      onChange(key, val as AgentConfig[K]);

  useEffect(() => {
    // Run in console:  debugAgentFrames()
    window.debugAgentFrames = () => {
      console.log('agentFrames:', config.frameIds);
      if (Array.isArray(config.frameIds)) console.table(config.frameIds);
    };
    return () => {
      delete window.debugAgentFrames;
    };
  }, [config.frameIds]);

  // Don't render until options arrive — avoids a flash of empty selects
  if (!options) return null;

  return (
    <>
      <h4 className="h4-title">Main Settings</h4>

      <div className="input-block d-flex flex-row justify-between">
        <div className="input-description">
          <div className="input-title">Name</div>
          <div className="small-font">Enter agent name</div>
        </div>
        <div className="align-wrapper d-flex align-center">
          <CustomInput
            id="agent-name"
            label="Agent Name"
            placeholder="Enter agent name"
            icon={<i className="fa-solid fa-user"></i>}
            value={config.name}
            onChange={(_, value) => onChange('name', value)}
            disabled={isDisabled}
          />
        </div>
      </div>

      {isOrganizationsPermission && (
        <>
          <div className="input-block d-flex flex-row justify-between">
            <div className="input-description">
              <div className="input-title">Model</div>
              <div className="small-font">Select AI model</div>
            </div>
            <div className="align-wrapper d-flex align-center">
              <CustomSelect
                id="agent-model-select"
                label="Models"
                options={options.models}
                value={config.modelName}
                multiSelect={false}
                onChange={onSelect('modelName')}
                disabled={isDisabled}
              />
            </div>
          </div>
          {availableReasoningOptions.length > 0 && (
            <div className="input-block d-flex flex-row justify-between">
              <div className="input-description">
                <div className="input-title">Reasoning</div>
                <div className="small-font">Select AI reasoning</div>
              </div>
              <div className="align-wrapper d-flex align-center">
                <CustomSelect
                  id="agent-model-reasoning"
                  label="Reasoning"
                  options={availableReasoningOptions}
                  value={config.reasoning}
                  multiSelect={false}
                  onChange={onSelect('reasoning')}
                  disabled={isDisabled}
                />
              </div>
            </div>
          )}
        </>
      )}

      <div className="input-block d-flex flex-row justify-between">
        <div className="input-description">
          <div className="input-title">Agent Skills</div>
          <div className="small-font">Select agent skills</div>
        </div>
        <div className="align-wrapper d-flex align-center">
          <CustomSelect
            id="agent-frames-select"
            label="Agent Skills"
            options={availableSkills}
            value={config.skills}
            multiSelect={true}
            onChange={onSelect('skills')}
            disabled={isDisabled}
          />
        </div>
      </div>

      <div className="input-block d-flex flex-row justify-between">
        <div className="input-description">
          <div className="input-title">Level of Details</div>
          <div className="small-font">Select detalization of responses made by the agent</div>
        </div>
        <div className="align-wrapper d-flex align-center">
          <CustomSelect
            id="details-level-select"
            label="Level of Details"
            options={options.details_levels}
            value={config.detailsLevel}
            multiSelect={false}
            onChange={onSelect('detailsLevel')}
            disabled={isDisabled}
          />
        </div>
      </div>

      {showTemperature && (
        <div className="input-block d-flex flex-row justify-between">
          <div className="input-description">
            <div className="input-title">Temperature</div>
            <div className="small-font">
              Set the temperature ({options.temperature.min}-{options.temperature.max})
            </div>
          </div>
          <div className="align-wrapper d-flex align-center">
            <CustomSlider
              id="temperature-slider"
              label="Temperature"
              min={options.temperature.min}
              max={options.temperature.max}
              oneStep={options.temperature.step}
              suffix=" %"
              value={config.temperature}
              onChange={(val) => onChange('temperature', val)}
              disabled={isDisabled}
            />
          </div>
        </div>
      )}

      <div className="input-block d-flex flex-row justify-between">
        <div className="input-description">
          <div className="input-title">Prompt</div>
          <div className="small-font">Select a prompt</div>
        </div>
        <div className="align-wrapper d-flex align-center">
          <CustomSelect
            id="prompt-select"
            label="Prompt"
            options={(data.prompts || []).map((a) => ({
              value: a.id,
              label: a.name,
            }))}
            value={config.promptId}
            multiSelect={false}
            onChange={onSelect('promptId')}
            disabled={isDisabled}
          />
        </div>
      </div>

      <div className="input-block d-flex flex-row justify-between">
        <div className="input-description">
          <div className="input-title">Agent Frames</div>
          <div className="small-font">Select agent frames (optional)</div>
        </div>
        <AgentFramesPicker
          value={config.frameIds}
          onChange={(frameIds) => onChange('frameIds', frameIds)}
          selectedFrames={data.selectedFrames || []}
          fetchFrames={fetchFrames}
          disabled={isDisabled}
        />
      </div>

      <div className="input-block d-flex flex-row justify-between">
        <div className="input-description">
          <div className="input-title">Agent Markers</div>
          <div className="small-font">Select agent markers (optional)</div>
        </div>
        <div className="align-wrapper d-flex align-center">
          <CustomSelect
            id="agent-markers"
            label="Agent Markers"
            options={(data.markers || []).map((m) => ({
              value: m.id,
              label: m.name,
            }))}
            value={config.markerIds}
            multiSelect={true}
            onChange={onSelect('markerIds')}
            disabled={isDisabled}
          />
        </div>
      </div>

      <div className="input-block d-flex flex-row justify-between">
        <div className="input-description">
          <div className="input-title">Swarm</div>
          <div className="small-font">Select swarm agents (optional)</div>
        </div>
        <div className="align-wrapper d-flex align-center">
          <CustomSelect
            id="swarm-select"
            label="Swarm"
            options={(data.swarms || []).map((s) => ({
              value: s.key,
              label: s.name,
            }))}
            value={config.swarmKeys}
            multiSelect={true}
            onChange={onSelect('swarmKeys')}
            disabled={isDisabled}
          />
        </div>
      </div>
      {isNotificationFlag && (
        <div className="input-block d-flex flex-row justify-between">
          <div className="input-description">
            <div className="input-title">Notifications</div>
            <div className="small-font">Select notifications (optional)</div>
          </div>
          <div className="align-wrapper d-flex align-center">
            <CustomSelect
              id="notifications-select"
              label="Notifications"
              options={(data.notifications || []).map((a) => ({
                value: a.id,
                label: a.name,
              }))}
              value={config.notificationIds}
              multiSelect={true}
              onChange={onSelect('notificationIds')}
              disabled={isDisabled}
            />
          </div>
        </div>
      )}

      <div className="input-block d-flex flex-row justify-between">
        <div className="input-description">
          <div className="input-title">Type</div>
          <div className="small-font">Select the agent type</div>
        </div>
        <div className="align-wrapper d-flex align-center">
          <CustomSelect
            id="type-select"
            label="Type"
            options={options.agent_types}
            value={config.agentType}
            multiSelect={false}
            onChange={onSelect('agentType')}
            disabled={isDisabled}
          />
        </div>
      </div>

      {config.agentType === 'RAG' && isDocumentFlag && (
        <div className="input-block d-flex flex-row justify-between">
          <div className="input-description">
            <div className="input-title">Collections</div>
            <div
              className="small-font"
              style={{ color: collectionTextWarning ? '#b00020' : 'inherit' }}
            >
              {collectionTextWarning || 'Select collections (optional)'}
            </div>
          </div>
          <div className="align-wrapper d-flex align-center">
            <CustomSelect
              id="collections-select"
              label="Collections"
              options={(data.documentCollections || []).map((a) => ({
                value: a.id,
                label: a.name,
              }))}
              value={config.documentCollectionIds}
              multiSelect={true}
              maxItems={2}
              onChange={onSelect('documentCollectionIds')}
              disabled={isDisabled || disableCollections}
            />
          </div>
        </div>
      )}
      {config.agentType === 'RAG' && isInformationPackageFlag && (
        <div className="input-block d-flex flex-row justify-between">
          <div className="input-description">
            <div className="input-title">IP Collections</div>
            <div className="small-font">Select IP collections (optional)</div>
          </div>
          <div className="align-wrapper d-flex align-center">
            <CustomSelect
              id="information-packages-select"
              label="IP Collections"
              options={(data.informationPackageCollections || []).map((a) => ({
                value: a.id,
                label: a.name,
              }))}
              value={config.informationPackageCollectionIds}
              multiSelect={true}
              onChange={onSelect('informationPackageCollectionIds')}
              disabled={isDisabled}
            />
          </div>
        </div>
      )}
      {isConnectionFlag && (
        <div className="input-block d-flex flex-row justify-between">
          <div className="input-description">
            <div className="input-title">Connections</div>
            <div className="small-font">Select connections (optional)</div>
          </div>
          <div className="align-wrapper d-flex align-center">
            <CustomSelect
              id="connections-select"
              label="Connections"
              options={(data.connections || []).map((a) => ({
                value: a.id,
                label: a.name,
              }))}
              value={config.connectionIds}
              multiSelect={true}
              maxItems={100}
              onChange={onSelect('connectionIds')}
              disabled={isDisabled}
            />
          </div>
        </div>
      )}

      {isDocumentPermission && isProductFlag && (
        <div className="input-block d-flex flex-row justify-between">
          <div className="input-description">
            <div className="input-title">Product Catalog</div>
            <div className="small-font">Select the product catalog</div>
          </div>
          <div className="align-wrapper d-flex align-center">
            <CustomSelect
              id="catalog-select"
              label="Product Catalog"
              options={[
                { value: null, label: 'Not Selected' },
                ...(data.catalogs || []).map((item) => ({ value: item.id, label: item.name })),
              ]}
              value={config.catalog}
              multiSelect={false}
              onChange={onSelect('catalog')}
              disabled={isDisabled}
            />
          </div>
        </div>
      )}

      <div className="input-block d-flex flex-row justify-between">
        <div className="input-description">
          <div className="input-title">Dictionaries</div>
          <div className="small-font">Select dictionary (optional)</div>
        </div>
        <div className="align-wrapper d-flex align-center">
          <CustomSelect
            id="dictionary-select"
            label="Dictionaries"
            options={(data.dictionaries || []).map((d) => ({ value: d.id, label: d.title }))}
            value={config.dictionaryId}
            multiSelect={false}
            onChange={onSelect('dictionaryId')}
            disabled={isDisabled}
          />
        </div>
      </div>

      <div className="input-block d-flex flex-row justify-between">
        <div className="input-description">
          <div className="input-title">Templates</div>
          <div className="small-font">Select template (optional)</div>
        </div>
        <div className="align-wrapper d-flex align-center">
          <CustomSelect
            id="template-select"
            label="Templates"
            options={(data.templates || []).map((d) => ({ value: d.id, label: d.name }))}
            value={config.templateIds}
            multiSelect={true}
            onChange={onSelect('templateIds')}
            disabled={isDisabled}
          />
        </div>
      </div>

      <div className="input-block d-flex flex-row justify-between">
        <div className="input-description">
          <div className="input-title">Active</div>
          <div className="small-font">Set the agent to be active or inactive</div>
        </div>
        <div className="align-wrapper d-flex align-center">
          <CustomToggle
            id="active-toggle"
            label={config.isActive ? 'Active' : 'Inactive'}
            checked={config.isActive}
            onChange={(_, value) => onChange('isActive', value)}
            disabled={isDisabled}
          />
        </div>
      </div>

      <div className="input-block d-flex flex-row justify-between">
        <div className="input-description">
          <div className="input-title">Memory Type</div>
          <div className="small-font">Select agent memory type (optional)</div>
        </div>
        <div className="align-wrapper d-flex align-center">
          <CustomSelect
            id="memory-type"
            label="Memory Type"
            options={options.memory_types}
            value={config.memoryType}
            multiSelect={false}
            onChange={onSelect('memoryType')}
            disabled={isDisabled}
          />
        </div>
      </div>

      <div className="input-block d-flex flex-row justify-between">
        <div className="input-description">
          <div className="input-title">Audit Execution</div>
          <div className="small-font">Set the audit execution enable or disable</div>
        </div>
        <div className="align-wrapper d-flex align-center">
          <CustomToggle
            id="audit-toggle"
            label={config.auditExecution ? 'Enable' : 'Disabled'}
            checked={config.auditExecution}
            onChange={(_, value) => onChange('auditExecution', value)}
            disabled={isDisabled}
          />
        </div>
      </div>

      {config.memoryType && (
        <div className="input-block d-flex flex-row justify-between">
          <div className="input-description">
            <div className="input-title">Memory Limit</div>
            <div className="small-font">
              {config.memoryType === 'MESSAGE_COUNT'
                ? 'Enter the number of messages the agent will remember'
                : 'Enter the number of hours the agent will remember'}
            </div>
          </div>
          <div className="align-wrapper d-flex align-center">
            <CustomNumberInput
              id="memory-limit-input"
              value={config.memoryLimit}
              max={options.memory_limit.max}
              min={options.memory_limit.min}
              onChange={(val) => onChange('memoryLimit', val)}
              disabled={isDisabled}
            />
          </div>
        </div>
      )}
    </>
  );
}

export default AgentSettings;
